import React, { useEffect, useState } from 'react';
import { Plus, X, Pencil, Trash, Plugs } from '@phosphor-icons/react';
import {
  fetchEntries, createEntry, updateEntry, deleteEntry, testDbSlot,
} from '../api/connectionRegistryApi';
import { useSupportingTools } from '../SupportingToolsContext';
import { apiErrorMessage } from '../utils/auditUser';
import { useToast } from '../utils/useToast';
import Toast from '../components/Toast';

const EMPTY_FORM = {
  product_key: '',
  product_label: '',
  client_name: '',
  api_url: 'http://easychit-api',
  db_server: '',
  db_port: '5434',
  db_name: '',
  db_user: '',
  db_password: '',
  db2_server: '',
  db2_port: '5434',
  db2_name: '',
  db2_user: '',
  db2_password: '',
  global_schema: 'GLOBAL',
  scheme_name: 'GLOBAL',
};

function SlotTestBanner({ result, slot, label }) {
  if (!result || result.slot !== slot) return null;
  return (
    <div
      style={{
        marginTop: '12px',
        padding: '10px 12px',
        borderRadius: '6px',
        border: `1px solid ${result.ok ? '#A7F3D0' : '#FECACA'}`,
        background: result.ok ? '#ECFDF5' : '#FEF2F2',
        color: result.ok ? '#065F46' : '#991B1B',
        fontSize: '12px',
      }}
      data-testid={`connection-${slot}-test-result`}
    >
      {result.ok ? (
        <>
          {label} OK
          {result.database || result.db_name ? ` — ${result.database || result.db_name}` : ''}.
          {' '}API + database reachable.
        </>
      ) : (
        <>
          {label} failed.
          {' '}{result.error || 'Unknown error'}
        </>
      )}
    </div>
  );
}

function RegistryPage() {
  const { variant } = useSupportingTools();
  // Supporting Tools Live names its connections for what they are; Supporting
  // Tools keeps Connection1 / Connection2.
  const isLive = variant === 'live';
  const primaryName = isLive ? 'Live Connection' : 'Connection1';
  const secondaryName = isLive ? 'Live Connection (Secondary)' : 'Connection2';
  const { toast, setToast, showToast } = useToast();
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [testingSlot, setTestingSlot] = useState(null);
  const [slotTestPrimary, setSlotTestPrimary] = useState(null);
  const [slotTestSecondary, setSlotTestSecondary] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      setEntries(await fetchEntries(variant));
    } catch (e) {
      console.error('fetchEntries', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const clearSlotTests = () => {
    setSlotTestPrimary(null);
    setSlotTestSecondary(null);
    setTestingSlot(null);
  };

  const openCreate = () => {
    setEditing(null);
    setFormData(EMPTY_FORM);
    clearSlotTests();
    setShowModal(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setFormData({
      product_key: row.product_key,
      product_label: row.product_label,
      client_name: row.client_name,
      api_url: row.api_url,
      db_server: row.db_server || '',
      db_port: row.db_port || '5434',
      db_name: row.db_name || '',
      db_user: row.db_user || '',
      db_password: '',
      db2_server: row.db2_server || '',
      db2_port: row.db2_port || '5434',
      db2_name: row.db2_name || '',
      db2_user: row.db2_user || '',
      db2_password: '',
      global_schema: row.global_schema || 'GLOBAL',
      scheme_name: row.scheme_name || row.global_schema || 'GLOBAL',
    });
    clearSlotTests();
    setShowModal(true);
  };

  const buildPayload = () => ({
    product_key: formData.product_key.trim(),
    product_label: formData.product_label.trim(),
    client_name: formData.client_name.trim(),
    api_url: formData.api_url.trim(),
    db_server: formData.db_server.trim() || null,
    db_port: (formData.db_port || '5434').trim() || '5434',
    db_name: formData.db_name.trim() || null,
    db_user: formData.db_user.trim() || null,
    db_password: formData.db_password,
    db2_server: formData.db2_server.trim() || null,
    db2_port: (formData.db2_port || '').trim() || null,
    db2_name: formData.db2_name.trim() || null,
    db2_user: formData.db2_user.trim() || null,
    db2_password: formData.db2_password,
    global_schema: (formData.global_schema || 'GLOBAL').trim() || 'GLOBAL',
    scheme_name: (formData.scheme_name || formData.global_schema || 'GLOBAL').trim() || 'GLOBAL',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = buildPayload();
    try {
      if (editing) {
        await updateEntry(editing.connection_id, payload);
      } else {
        await createEntry(payload, variant);
      }
      setShowModal(false);
      load();
    } catch (err) {
      showToast('error', 'Save Failed', apiErrorMessage(err, 'Could not save the connection.'), true);
    }
  };

  const confirmDelete = async () => {
    const row = deleteTarget;
    if (!row) return;
    setDeleteTarget(null);
    try {
      await deleteEntry(row.connection_id);
      load();
      showToast('success', 'Connection Deleted', `${row.client_name} (${row.product_label}) was removed.`);
    } catch (err) {
      showToast('error', 'Delete Failed', apiErrorMessage(err, 'Could not delete the connection.'), true);
    }
  };

  const handleTestSlot = async (slot) => {
    const isSecondary = slot === 'secondary';
    setTestingSlot(slot);
    if (isSecondary) setSlotTestSecondary(null);
    else setSlotTestPrimary(null);

    const body = {
      slot,
      api_url: formData.api_url.trim(),
      connection_id: editing?.connection_id || null,
      db_server: isSecondary ? formData.db2_server.trim() : formData.db_server.trim(),
      db_port: isSecondary
        ? (formData.db2_port || '5434').trim()
        : (formData.db_port || '5434').trim(),
      db_name: isSecondary ? formData.db2_name.trim() : formData.db_name.trim(),
      db_user: isSecondary ? formData.db2_user.trim() : formData.db_user.trim(),
      db_password: isSecondary ? formData.db2_password : formData.db_password,
      global_schema: (formData.global_schema || 'GLOBAL').trim() || 'GLOBAL',
      scheme_name: (formData.scheme_name || formData.global_schema || 'GLOBAL').trim() || 'GLOBAL',
    };

    try {
      const result = await testDbSlot(body);
      if (isSecondary) setSlotTestSecondary(result);
      else setSlotTestPrimary(result);
    } catch (err) {
      const fail = {
        slot,
        ok: false,
        slot_label: isSecondary ? secondaryName : primaryName,
        error: err?.response?.data?.detail || 'Connection test failed',
      };
      if (isSecondary) setSlotTestSecondary(fail);
      else setSlotTestPrimary(fail);
    } finally {
      setTestingSlot(null);
    }
  };

  const canTestPrimary = Boolean(
    formData.api_url.trim()
    && (formData.db_server.trim() || editing?.db_server)
    && (formData.db_name.trim() || editing?.db_name),
  );
  const canTestSecondary = Boolean(
    formData.api_url.trim()
    && (formData.db2_server.trim() || editing?.db2_server)
    && (formData.db2_name.trim() || editing?.db2_name),
  );

  return (
    <div data-testid="connection-registry-page">
      <Toast toast={toast} onClose={() => setToast(null)} testId="registry-toast" />
      {deleteTarget && (
        <div
          onClick={() => setDeleteTarget(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: 'white', borderRadius: '10px', width: '420px', maxWidth: '90vw', overflow: 'hidden' }}
            data-testid="registry-delete-confirm-modal"
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #E5E7EB' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Trash size={18} color="#002FA7" />
                <h5 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#111827' }}>Confirm Delete Connection</h5>
              </div>
              <button type="button" onClick={() => setDeleteTarget(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF' }}>
                <X size={16} />
              </button>
            </div>
            <div style={{ padding: '20px' }}>
              <p style={{ fontSize: '13px', color: '#6B7280', marginTop: 0 }}>
                You&apos;re about to delete the connection for <strong>{deleteTarget.client_name}</strong> ({deleteTarget.product_label}).
              </p>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '14px 20px', borderTop: '1px solid #E5E7EB' }}>
              <button type="button" onClick={() => setDeleteTarget(null)} className="btn-secondary" data-testid="registry-delete-cancel">Cancel</button>
              <button type="button" onClick={confirmDelete} className="btn-primary" data-testid="registry-delete-confirm">Delete</button>
            </div>
          </div>
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#111827', marginBottom: '4px' }}>Connection Registry</h2>
          <p style={{ fontSize: '13px', color: '#6B7280' }}>
            Configure EasyChit API + Postgres here. Test {primaryName} and {secondaryName} separately in the form.
          </p>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="btn-primary"
          style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
          data-testid="create-connection-button"
        >
          <Plus size={18} weight="bold" />
          Add Connection
        </button>
      </div>

      {loading ? (
        <div>Loading...</div>
      ) : entries.length === 0 ? (
        <div style={{ padding: '24px', background: 'white', border: '1px solid #E5E7EB', color: '#6B7280', fontSize: '14px' }} data-testid="connections-empty">
          No connections registered yet. Use &quot;Add Connection&quot; to create one.
        </div>
      ) : (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Product</th>
                <th>Client</th>
                <th>API URL</th>
                <th>Database</th>
                <th>Schema</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((row) => (
                <tr key={row.connection_id} data-testid="connection-row">
                  <td>{row.product_label}</td>
                  <td style={{ fontWeight: '600' }}>{row.client_name}</td>
                  <td style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: '12px', color: '#6B7280' }}>{row.api_url}</td>
                  <td style={{ fontSize: '12px', color: '#6B7280' }}>
                    {row.db_name
                      ? `${row.db_name}@${row.db_server || '?'}:${row.db_port || '5432'}`
                      : (row.has_db_password || row.connection_string ? '••••••' : '—')}
                    {row.db2_name ? (
                      <div style={{ marginTop: '2px', color: '#9CA3AF' }}>
                        + {row.db2_name}@{row.db2_server || '?'}:{row.db2_port || '5434'}
                      </div>
                    ) : null}
                  </td>
                  <td style={{ fontSize: '12px', color: '#6B7280' }}>{row.global_schema || 'GLOBAL'}</td>
                  <td>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        onClick={() => openEdit(row)}
                        className="btn-secondary"
                        style={{ padding: '4px 10px', fontSize: '12px', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                        data-testid="edit-connection-button"
                      >
                        <Pencil size={12} /> Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeleteTarget(row)}
                        className="btn-secondary"
                        style={{ padding: '4px 10px', fontSize: '12px', color: '#B91C1C', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                        data-testid="delete-connection-button"
                      >
                        <Trash size={12} /> Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.5)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 1000,
            padding: '16px',
          }}
          data-testid="connection-modal"
        >
          <div style={{
            background: 'white', padding: '28px', width: '100%', maxWidth: '680px',
            maxHeight: '90vh', overflowY: 'auto', borderRadius: '10px',
          }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ fontSize: '18px', fontWeight: '600', margin: 0 }}>
                {editing ? `Edit connection for "${editing.client_name}"` : 'Add New Connection'}
              </h2>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}
                data-testid="connection-modal-close"
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>Product Key</label>
                  <input
                    type="text"
                    value={formData.product_key}
                    onChange={(e) => setFormData({ ...formData, product_key: e.target.value })}
                    className="input-field"
                    placeholder="e.g. easy_chit"
                    required
                    data-testid="connection-product-key-input"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>Product Label</label>
                  <input
                    type="text"
                    value={formData.product_label}
                    onChange={(e) => setFormData({ ...formData, product_label: e.target.value })}
                    className="input-field"
                    placeholder="e.g. Easy Chit"
                    required
                    data-testid="connection-product-label-input"
                  />
                </div>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>Client Name</label>
                <input
                  type="text"
                  value={formData.client_name}
                  onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                  className="input-field"
                  placeholder="e.g. KGMS"
                  required
                  data-testid="connection-client-name-input"
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>API URL</label>
                <input
                  type="text"
                  value={formData.api_url}
                  onChange={(e) => setFormData({ ...formData, api_url: e.target.value })}
                  className="input-field"
                  placeholder="http://easychit-api"
                  required
                  data-testid="connection-api-url-input"
                />
                <p style={{ fontSize: '11px', color: '#6B7280', marginTop: '4px' }}>
                  Docker Service Name: <code>http://easychit-api</code>
                </p>
              </div>

              <div style={{
                marginBottom: '16px', padding: '14px', border: '1px solid #E5E7EB',
                borderRadius: '8px', background: '#F9FAFB',
              }}
              >
                <div style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  marginBottom: '10px', gap: '8px', flexWrap: 'wrap',
                }}
                >
                  <div style={{ fontSize: '13px', fontWeight: 600, color: '#111827' }}>
                    {isLive ? primaryName : 'Connection1 — Primary Postgres (EasyChit Con)'}
                  </div>
                  <button
                    type="button"
                    onClick={() => handleTestSlot('primary')}
                    disabled={!canTestPrimary || testingSlot === 'primary'}
                    className="btn-secondary"
                    style={{
                      padding: '4px 10px', fontSize: '12px',
                      display: 'inline-flex', alignItems: 'center', gap: '4px',
                    }}
                    data-testid="test-connection1-button"
                  >
                    <Plugs size={12} />
                    {testingSlot === 'primary' ? 'Testing…' : `Test ${primaryName}`}
                  </button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '12px', marginBottom: '12px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: '500' }}>Server / Host</label>
                    <input
                      type="text"
                      value={formData.db_server}
                      onChange={(e) => setFormData({ ...formData, db_server: e.target.value })}
                      className="input-field"
                      placeholder="e.g. 13.232.251.205"
                      required={!editing}
                      data-testid="connection-db-server-input"
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: '500' }}>Port</label>
                    <input
                      type="text"
                      value={formData.db_port}
                      onChange={(e) => setFormData({ ...formData, db_port: e.target.value })}
                      className="input-field"
                      placeholder="5434"
                      data-testid="connection-db-port-input"
                    />
                  </div>
                </div>
                <div style={{ marginBottom: '12px' }}>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: '500' }}>Database Name</label>
                  <input
                    type="text"
                    value={formData.db_name}
                    onChange={(e) => setFormData({ ...formData, db_name: e.target.value })}
                    className="input-field"
                    placeholder="e.g. 226"
                    required={!editing}
                    data-testid="connection-db-name-input"
                  />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: '500' }}>User</label>
                    <input
                      type="text"
                      value={formData.db_user}
                      onChange={(e) => setFormData({ ...formData, db_user: e.target.value })}
                      className="input-field"
                      placeholder="postgres"
                      required={!editing}
                      autoComplete="off"
                      data-testid="connection-db-user-input"
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: '500' }}>Password</label>
                    <input
                      type="password"
                      value={formData.db_password}
                      onChange={(e) => setFormData({ ...formData, db_password: e.target.value })}
                      className="input-field"
                      placeholder={editing?.has_db_password ? 'Leave blank to keep existing' : '••••••••'}
                      required={!editing}
                      autoComplete="new-password"
                      data-testid="connection-db-password-input"
                    />
                  </div>
                </div>
                <SlotTestBanner result={slotTestPrimary} slot="primary" label={primaryName} />
              </div>

              <div style={{
                marginBottom: '16px', padding: '14px', border: '1px solid #E5E7EB',
                borderRadius: '8px', background: '#F9FAFB',
              }}
              >
                <div style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  marginBottom: '6px', gap: '8px', flexWrap: 'wrap',
                }}
                >
                  <div style={{ fontSize: '13px', fontWeight: 600, color: '#111827' }}>
                    {isLive ? secondaryName : 'Connection2 — Secondary Postgres (EasyChit Con1)'}
                  </div>
                  <button
                    type="button"
                    onClick={() => handleTestSlot('secondary')}
                    disabled={!canTestSecondary || testingSlot === 'secondary'}
                    className="btn-secondary"
                    style={{
                      padding: '4px 10px', fontSize: '12px',
                      display: 'inline-flex', alignItems: 'center', gap: '4px',
                    }}
                    data-testid="test-connection2-button"
                  >
                    <Plugs size={12} />
                    {testingSlot === 'secondary' ? 'Testing…' : `Test ${secondaryName}`}
                  </button>
                </div>
                <p style={{ fontSize: '11px', color: '#6B7280', margin: '0 0 10px' }}>
                  Optional. Used for dual-write updates (name / mobile / address). Leave blank if this client has one database only.
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '12px', marginBottom: '12px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: '500' }}>Server / Host</label>
                    <input
                      type="text"
                      value={formData.db2_server}
                      onChange={(e) => setFormData({ ...formData, db2_server: e.target.value })}
                      className="input-field"
                      placeholder="e.g. same host or UNIQUE_SUBSCRIBER host"
                      data-testid="connection-db2-server-input"
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: '500' }}>Port</label>
                    <input
                      type="text"
                      value={formData.db2_port}
                      onChange={(e) => setFormData({ ...formData, db2_port: e.target.value })}
                      className="input-field"
                      placeholder="5434"
                      data-testid="connection-db2-port-input"
                    />
                  </div>
                </div>
                <div style={{ marginBottom: '12px' }}>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: '500' }}>Database Name</label>
                  <input
                    type="text"
                    value={formData.db2_name}
                    onChange={(e) => setFormData({ ...formData, db2_name: e.target.value })}
                    className="input-field"
                    placeholder="e.g. UNIQUE_SUBSCRIBER"
                    data-testid="connection-db2-name-input"
                  />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: '500' }}>User</label>
                    <input
                      type="text"
                      value={formData.db2_user}
                      onChange={(e) => setFormData({ ...formData, db2_user: e.target.value })}
                      className="input-field"
                      placeholder="postgres"
                      autoComplete="off"
                      data-testid="connection-db2-user-input"
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: '500' }}>Password</label>
                    <input
                      type="password"
                      value={formData.db2_password}
                      onChange={(e) => setFormData({ ...formData, db2_password: e.target.value })}
                      className="input-field"
                      placeholder={editing?.has_db2_password ? 'Leave blank to keep existing' : '••••••••'}
                      autoComplete="new-password"
                      data-testid="connection-db2-password-input"
                    />
                  </div>
                </div>
                <SlotTestBanner result={slotTestSecondary} slot="secondary" label={secondaryName} />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>Global Schema</label>
                  <input
                    type="text"
                    value={formData.global_schema}
                    onChange={(e) => setFormData({ ...formData, global_schema: e.target.value })}
                    className="input-field"
                    placeholder="GLOBAL"
                    data-testid="connection-global-schema-input"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>Scheme Name</label>
                  <input
                    type="text"
                    value={formData.scheme_name}
                    onChange={(e) => setFormData({ ...formData, scheme_name: e.target.value })}
                    className="input-field"
                    placeholder="GLOBAL"
                    data-testid="connection-scheme-name-input"
                  />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px' }}>
                <button type="submit" className="btn-primary" style={{ flex: 1 }} data-testid="connection-submit-button">
                  {editing ? 'Save Changes' : 'Create Connection'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="btn-secondary"
                  style={{ flex: 1 }}
                  data-testid="connection-cancel-button"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default RegistryPage;
